import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Generic hook for async data fetching with loading/error state management.
 *
 * Replaces the common pattern:
 *   const [data, setData] = useState(null);
 *   const [loading, setLoading] = useState(true);
 *   const [error, setError] = useState<string | null>(null);
 *
 * @param fetchFn - Async function that returns the data. Return `undefined` to skip (e.g., when deps aren't ready).
 * @param deps - Dependency array that triggers re-fetch when values change.
 * @param options - Optional configuration.
 *
 * @example
 * ```tsx
 * const { data: plan, loading, error, refetch } = useAsyncState(
 *   () => fetch(`/api/journeys/${id}/action-plan`).then(r => r.json()),
 *   [id]
 * );
 * ```
 */
export function useAsyncState<T>(
  fetchFn: () => Promise<T | undefined>,
  deps: React.DependencyList,
  options?: {
    /** If true, don't fetch on mount — only on explicit refetch() calls. Default: false */
    manual?: boolean;
    /** Initial loading state. Default: true */
    initialLoading?: boolean;
  }
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(options?.initialLoading ?? true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const execute = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchFn();
      if (!mountedRef.current) return;
      if (result !== undefined) {
        setData(result);
      }
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    mountedRef.current = true;
    if (!options?.manual) {
      execute();
    }
    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execute]);

  return { data, loading, error, refetch: execute, setData };
}
